import type { Schema } from "hast-util-sanitize";

/**
 * Allowlist for the canonical rich-text tag set. This is the sole authority for
 * what survives sanitization; `rehype-canonicalize` runs first to convert the
 * demoted tags (h4-6, blockquote/pre/code, hr) and unwrap div/font so that by
 * the time this schema applies, the tree only contains tags to keep or drop.
 *
 * Attribute policy: nothing is allowed globally (`"*": []`), so every `style`,
 * `class`, and `data-*` attribute is stripped. Only `a`, `img`, and `iframe`
 * carry a small explicit set. Note the keys are hast *property* names (camelCase:
 * `referrerPolicy`, `allowFullScreen`), not lowercase HTML attribute names —
 * `hast-util-sanitize` matches on the property name, so a lowercased key silently
 * strips the attribute.
 *
 * `img`/`iframe`/`figure`/`table` are allowed in EVERY context so legacy content
 * (uploaded images, embedded videos, tables) renders. Whether a user can *add*
 * them is an editor affordance (see `editor-contexts`), not a sanitizer rule — a
 * pasted image is allowed to survive.
 */
export const canonicalRichTextSchema: Schema = {
  // Drop these elements AND their text content (default only strips <script>).
  strip: ["script", "style", "title", "embed", "object"],
  // Neutralize any leftover id/name collisions rather than trusting them.
  clobberPrefix: "user-content-",
  clobber: [],
  protocols: {
    href: ["http", "https", "mailto", "tel"],
    src: ["http", "https"],
  },
  tagNames: [
    // Base set — allowed on every field.
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "del",
    "s",
    "ul",
    "ol",
    "li",
    "a",
    "h1",
    "h2",
    "h3",
    // Media + tables — pass through for view in every context.
    "img",
    "iframe",
    "figure",
    "figcaption",
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "td",
    "th",
  ],
  attributes: {
    a: ["href", "target", "rel"],
    img: ["src", "alt", "width", "height"],
    // `sandbox` + `referrerPolicy` are forced on in `rehype-canonicalize` (they
    // must be allowlisted here to survive). Property-name casing matters — see
    // the note above (`allowFullScreen`/`frameBorder`, not lowercase).
    iframe: [
      "src",
      "width",
      "height",
      "frameBorder",
      "allowFullScreen",
      "sandbox",
      "referrerPolicy",
    ],
    // No global attributes — strips style, class, data-*, id, etc. everywhere.
    "*": [],
  },
};
